import { AlertModal, LoadingScreen } from "@administrate/piston-ux";
import Auth0Client from "@auth0/auth0-spa-js/dist/typings/Auth0Client";
import {
  GetTokenSilentlyOptions,
  LogoutOptions,
  User,
} from "@auth0/auth0-spa-js/dist/typings/global";
import * as Sentry from "@sentry/browser";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import createAuth0Client from "./auth0-spa-js.production.esm";
import { clearBookerIntention } from "./hooks/useBookerIntention";
import { useGlobalStore } from "./providers/GlobalStore";

const onRedirectCallback = (appState: any) => {
  window.history.replaceState(
    {},
    document.title,
    appState && appState.targetUrl
      ? appState.targetUrl
      : window.location.pathname,
  );
};

type Auth0Context = {
  isAuthenticated: boolean;
  user: User | undefined;
  loading: boolean;
  handleRedirectCallback: () => void;
  getIdTokenClaims: Auth0Client["getIdTokenClaims"];
  loginWithRedirect: Auth0Client["loginWithRedirect"];
  getTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>;
  getTokenWithPopup: Auth0Client["getTokenWithPopup"];
  logout: (
    options?: LogoutOptions & { redirect_uri: string; brand: string },
  ) => ReturnType<Auth0Client["logout"]>;
};

type Auth0ProviderProps = React.PropsWithChildren<{
  domain: string;
  issuer: string;
  client_id: string;
  redirect_uri: string;
  returnTo: string;
  brand: string;
  leeway: number;
  useRefreshTokens: boolean;
  cacheLocation: string;
}>;

export const Auth0Context = React.createContext<Auth0Context | undefined>(
  undefined,
);
export const useAuth0 = () => {
  const context = useContext(Auth0Context);
  if (context === undefined) {
    throw new Error("Auth0 context not initialised");
  }
  return context;
};
export const Auth0Provider = ({
  children,
  ...initOptions
}: Auth0ProviderProps) => {
  const { config } = useGlobalStore();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | undefined>();
  const [auth0Client, setAuth0] = useState<Auth0Client>();
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string>();

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook: Auth0Client = await createAuth0Client(initOptions);
      setAuth0(auth0FromHook);

      if (window.location.search.includes("code=")) {
        try {
          const { appState } = await auth0FromHook.handleRedirectCallback();
          onRedirectCallback(appState);
        } catch (error: any) {
          if (error?.message === "Invalid state") {
            await auth0FromHook.loginWithRedirect();
          } else {
            Sentry.captureException(error);
            setAuthError("authenticateError");
          }
        }
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated();

      setIsAuthenticated(isAuthenticated);

      if (isAuthenticated) {
        const user = await auth0FromHook.getUser();
        setUser(user);
      }

      setLoading(false);
    };
    initAuth0().catch((error) => {
      if (error?.name === "SecurityError" && /storage/i.test(error.message)) {
        setAuthError("authenticateStorageError");
      } else {
        Sentry.captureException(error);
        setAuthError("authenticateError");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client!.handleRedirectCallback();
    const user = await auth0Client!.getUser();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(user);
  };

  const handleClickTryAgain = async () => {
    window.location.href = "/";
  };

  const { t } = useTranslation();
  if (authError) {
    return (
      <div className="login-page">
        <AlertModal
          title={t(authError)}
          show={true}
          onDone={() => handleClickTryAgain()}
          type="warningWithoutCancel"
        />
      </div>
    );
  }

  if (loading) return <LoadingScreen />;

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        handleRedirectCallback,
        getIdTokenClaims: (...p) => auth0Client!.getIdTokenClaims(...p),
        loginWithRedirect: (...p) => auth0Client!.loginWithRedirect(...p),
        getTokenSilently: (...p) => auth0Client!.getTokenSilently(...p),
        getTokenWithPopup: (...p) => auth0Client!.getTokenWithPopup(...p),
        logout: (...p) => {
          clearBookerIntention(config);
          auth0Client!.logout(...p);
        },
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
